import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ticketImages } from '@/assets/img/lottery';
import { type TicketsReward } from '@/common/types/lottery';
import { type ArenaPackRewardDto } from '@/common/types/shop/marketBoxes';
import { StringSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import usdPng from '/img/icons/usd-large.png';
import usdWebp from '/img/icons/usd-large.webp';
import arenaOfferPng from '/img/shop/arenaOffers/profitDropArenaOffer.png';
import arenaOfferWebp from '/img/shop/arenaOffers/profitDropArenaOffer.webp';
import greenBg from '/img/shop/arenaOffers/profitDrops/bg-green.png';
import purpleBg from '/img/shop/arenaOffers/profitDrops/bg-purple.png';
import yellowBg from '/img/shop/arenaOffers/profitDrops/bg-yellow.png';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

type variant = 'arenaOffer' | 'lottery' | 'tgem' | 'usdt';

type size = 'sm' | 'lg';

const images: Record<variant, string> = {
  'tgem': purpleBg,
  'arenaOffer': yellowBg,
  'usdt': greenBg,
  'lottery': yellowBg,
};

const icons: Record<
  variant,
  { webp: string; png: string; className?: string }
> = {
  'tgem': {
    png: tgemPng,
    webp: tgemWebp,
    className: 'size-[78%]',
  },
  'arenaOffer': {
    png: arenaOfferPng,
    webp: arenaOfferWebp,
    className: 'h-[78%]',
  },
  'usdt': {
    png: usdPng,
    webp: usdWebp,
    className: 'size-[76%]',
  },
  'lottery': {
    ...ticketImages.gold3d,
    className: 'size-[80%]',
  },
};

const textSizes: Record<size, Partial<Record<variant | 'default', string>>> = {
  sm: {
    default: '!text-[0.875rem]',
    arenaOffer: '!text-[0.75rem]',
    lottery: '!text-[0.75rem]',
  },
  lg: {
    default: '!text-[1.125rem]',
  },
};

interface RewardProps {
  type: variant;
  value: number | ArenaPackRewardDto | Partial<TicketsReward> | undefined;
  size?: size;
  className?: string;
}

const ProfitDropRewardCard: FC<RewardProps> = ({
  type,
  value,
  size = 'sm',
  className,
}) => {
  const { t } = useTranslation();
  const getAmountFromValue = (
    value: number | ArenaPackRewardDto | Partial<TicketsReward> | undefined,
  ): number => {
    const isClasicObject = typeof value === 'object' && value !== null;

    if (isClasicObject && 'goldTicket' in value) {
      return value.goldTicket || 0;
    }

    if (isClasicObject) {
      return 0;
    }

    return value || 0;
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center overflow-hidden relative aspect-square rounded-lg',
        type === 'lottery' ? 'bg-black/5' : 'bg-black/10',
        className,
      )}
    >
      <Picture
        img={images[type]}
        alt="background"
        className="inset-0 absolute z-[1]"
      />

      <Picture
        img={icons[type].png}
        webp={icons[type].webp}
        alt="icon"
        className={cn(icons[type].className, 'z-[2]')}
      />

      <StringSmall
        className={cn(
          'absolute py-0.5 px-1 bg-black/30 rounded-md backdrop-blur-lg !leading-none z-[3]',
          textSizes[size][type] ?? textSizes[size].default,
          size === 'sm' ? 'bottom-0.5' : 'bottom-3',
        )}
        weight="bold"
      >
        {type === 'arenaOffer' && t('Package')}
        {type === 'lottery' && getAmountFromValue(value)}
        {type === 'tgem' && ((value as number) ?? 0)}
        {type === 'usdt' && `$${(value as number) ?? 0}`}
      </StringSmall>
    </div>
  );
};

export default ProfitDropRewardCard;
